import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

interface AccountCarouselProps {
  accounts: Array<{ id: string; name: string; color: string }>;
  currentIndex: number; // -1 = "Tous les comptes", 0+ = compte spécifique
  onIndexChange: (index: number) => void;
  showAllOption?: boolean; // true = première position "Tous les comptes"
  compact?: boolean; // true = version compacte pour stat cards
}

export default function AccountCarousel({
  accounts,
  currentIndex,
  onIndexChange,
  showAllOption = false,
  compact = false,
}: AccountCarouselProps) {
  const { t } = useTranslation('dashboard');
  // Rien à afficher si pas de comptes
  if (accounts.length === 0) return null;

  // 1 seul compte sans option "Tous" → pas de carousel
  if (accounts.length === 1 && !showAllOption) return null;

  const totalPositions = showAllOption ? accounts.length + 1 : accounts.length;
  const minIndex = showAllOption ? -1 : 0;

  // Navigation circulaire
  const handlePrev = () => {
    if (currentIndex <= minIndex) {
      onIndexChange(accounts.length - 1);
    } else {
      onIndexChange(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex >= accounts.length - 1) {
      onIndexChange(minIndex);
    } else {
      onIndexChange(currentIndex + 1);
    }
  };

  const isAllAccounts = currentIndex === -1;
  const currentAccount = !isAllAccounts ? accounts[currentIndex] : null;

  // Position d'affichage (1-indexed)
  const displayPosition = showAllOption ? currentIndex + 2 : currentIndex + 1;

  const showArrows = totalPositions > 1;

  return (
    <div
      className={clsx(
        'flex items-center justify-center gap-2 select-none',
        compact ? 'py-1' : 'py-1.5'
      )}
    >
      {/* Flèche gauche */}
      {showArrows && (
        <button
          onClick={handlePrev}
          className={clsx(
            'flex-shrink-0 rounded-full transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100',
            compact ? 'p-0.5' : 'p-1'
          )}
          aria-label={t('accountCarousel.previousAccount')}
        >
          <ChevronLeft className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        </button>
      )}

      {/* Indicateur central */}
      <div
        className={clsx(
          'flex items-center justify-center gap-1.5',
          compact ? 'text-xs w-44' : 'text-sm w-52'
        )}
      >
        {isAllAccounts ? (
          <Layers className={clsx('flex-shrink-0 text-gray-500', compact ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
        ) : (
          <div
            className={clsx('flex-shrink-0 rounded-full', compact ? 'w-2.5 h-2.5' : 'w-3 h-3')}
            style={{ backgroundColor: currentAccount?.color }}
          />
        )}
        <span className="font-medium text-gray-700 truncate">
          {isAllAccounts ? t('accountCarousel.allAccounts') : currentAccount?.name}
        </span>
        {showArrows && (
          <span className="text-gray-400 flex-shrink-0">
            ({displayPosition}/{totalPositions})
          </span>
        )}
      </div>

      {/* Flèche droite */}
      {showArrows && (
        <button
          onClick={handleNext}
          className={clsx(
            'flex-shrink-0 rounded-full transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100',
            compact ? 'p-0.5' : 'p-1'
          )}
          aria-label={t('accountCarousel.nextAccount')}
        >
          <ChevronRight className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        </button>
      )}
    </div>
  );
}
